import { SentenceOffset } from '@chegg-wt/core/state/paraphraser/types'
import { AppState } from '@chegg-wt/core/state/types'
import { Dispatch } from 'redux'
import {
  feedbacksSelector,
  paperStatusSelector,
  sentenceOffsetsSelector,
} from '@chegg-wt/core/state/paraphraser/selectors'
import { adjustSentenceOffsets } from '@chegg-wt/core/state/paraphraser/actions'
import { PaperStatus } from '@chegg-wt/core/types/paraphraser'
import getFeatureEditor from '../../getEditor'
import { handleManualEdits } from '../../manualEditHelpers'
import { SentencesDelta } from '../types'
import { addNewFeedbacks } from './addNewFeedbacks'

export const filterSentenceOffsets = (
  sentenceOffsets: SentenceOffset[],
  sentencesToBeRemoved: SentencesDelta['removedSentences']
): SentenceOffset[] => {
  const filteredSentenceOffsets = []
  let removeSentenceIndex = 0

  sentenceOffsets.forEach((sentenceOffset) => {
    while (
      removeSentenceIndex < sentencesToBeRemoved.length &&
      sentencesToBeRemoved[removeSentenceIndex].offset < sentenceOffset.offset
    ) {
      removeSentenceIndex += 1
    }

    if (
      removeSentenceIndex < sentencesToBeRemoved.length &&
      sentenceOffset.id === sentencesToBeRemoved[removeSentenceIndex].id
    ) {
      return
    }

    filteredSentenceOffsets.push(sentenceOffset)
  })

  return filteredSentenceOffsets
}

export const addNewSentenceOffsets = (
  sentenceOffsets: SentenceOffset[],
  sentencesToBeAdded: SentencesDelta['addedSentences'],
  editorText: string
): SentenceOffset[] => {
  const updatedOffsets = []
  let addedSentenceIndex = 0
  let sentenceOffsetIndex = 0
  const timestamp = new Date().getTime()
  while (
    sentenceOffsetIndex < sentenceOffsets.length &&
    addedSentenceIndex < sentencesToBeAdded.length
  ) {
    if (
      sentenceOffsets[sentenceOffsetIndex].offset <
      sentencesToBeAdded[addedSentenceIndex].offset
    ) {
      updatedOffsets.push(sentenceOffsets[sentenceOffsetIndex])
      sentenceOffsetIndex += 1
    } else {
      const { offset, length } = sentencesToBeAdded[addedSentenceIndex]
      updatedOffsets.push({
        ...sentencesToBeAdded[addedSentenceIndex],
        originalText: editorText.substring(offset - 1, offset + length - 1), // offset is 1 based
        lastUpdated: timestamp,
      })
      addedSentenceIndex += 1
    }
  }
  while (sentenceOffsetIndex < sentenceOffsets.length) {
    updatedOffsets.push(sentenceOffsets[sentenceOffsetIndex])
    sentenceOffsetIndex += 1
  }
  while (addedSentenceIndex < sentencesToBeAdded.length) {
    const { offset, length } = sentencesToBeAdded[addedSentenceIndex]
    updatedOffsets.push({
      ...sentencesToBeAdded[addedSentenceIndex],
      originalText: editorText.substring(offset - 1, offset + length - 1), // offset is 1 based
      lastUpdated: timestamp,
    })
    addedSentenceIndex += 1
  }
  return updatedOffsets
}

export const applySentenceChange = (
  sentenceDelta: SentencesDelta,
  getState: () => AppState,
  dispatch: Dispatch,
  isUndo: boolean
) => {
  if (!sentenceDelta) return

  const state = getState()
  const sentenceOffsets = sentenceOffsetsSelector(state)
  const isDraftPaper = paperStatusSelector(state) === PaperStatus.DRAFT
  const feedbacks = feedbacksSelector(state)
  const editor = getFeatureEditor()
  const { addedSentences, removedSentences } = sentenceDelta

  const sentencesToBeRemoved = isUndo ? addedSentences : removedSentences
  const sentencesToBeAdded = isUndo ? removedSentences : addedSentences

  // Remove the added sentneces and it's feedbacks in non draft state
  if (!isDraftPaper) {
    handleManualEdits(
      sentencesToBeRemoved,
      sentenceOffsets, // Passing old setneces not updated one
      feedbacks,
      dispatch,
      editor,
      null
    )
  }
  // Not using .filter function as below is more optimized than that using property that sentencesToBeRemoved is sorted
  const filteredSentenceOffSets = filterSentenceOffsets(
    sentenceOffsets,
    sentencesToBeRemoved
  )
  const updatedSentences = addNewSentenceOffsets(
    filteredSentenceOffSets,
    sentencesToBeAdded,
    editor.getText()
  )

  dispatch(adjustSentenceOffsets(updatedSentences))

  if (!isDraftPaper) {
    const sentenceIds = new Set<string>()
    sentencesToBeAdded.forEach((sentence) => sentenceIds.add(sentence.id))
    addNewFeedbacks(
      sentenceIds,
      sentenceIds,
      getState(), // Again fetching state to get latest state with updated offsets
      dispatch
    )
  }
}

export const applySentenceOffsetOnManualChange = (
  removedSentences: SentencesDelta['removedSentences'],
  getState: () => AppState,
  dispatch: Dispatch,
  isUndo: boolean
) => {
  if (removedSentences.length === 0) return
  const state = getState()
  const sentenceOffsets = sentenceOffsetsSelector(state)
  const editor = getFeatureEditor()
  const updatedSentences = isUndo
    ? addNewSentenceOffsets(sentenceOffsets, removedSentences, editor.getText())
    : filterSentenceOffsets(sentenceOffsets, removedSentences)

  dispatch(adjustSentenceOffsets(updatedSentences))
}
